import { ActivitySummaryBucket, SummaryGranularity } from '../api/types';
import { addDaysIso, formatDuration, formatPace, isoLocal } from './format';

export type VolumeMetric = 'distance' | 'time' | 'runs';

interface GranularityConfig {
  label: string;
  /** Buckets visible at once. The chart pans over the rest of the fetched range. */
  windowBuckets: number;
  /** Horizontal pixels per bar. Bars keep this width, so the chart scrolls instead of squeezing. */
  slotPx: number;
}

export const GRANULARITY_CONFIG: Record<SummaryGranularity, GranularityConfig> = {
  week: { label: 'Weekly', windowBuckets: 26, slotPx: 24 },
  month: { label: 'Monthly', windowBuckets: 24, slotPx: 26 },
  year: { label: 'Yearly', windowBuckets: 10, slotPx: 56 },
};

export const GRANULARITIES: SummaryGranularity[] = ['week', 'month', 'year'];

export const METRIC_LABELS: Record<VolumeMetric, string> = {
  distance: 'Distance',
  time: 'Time',
  runs: 'Runs',
};

/** The API accepts ranges of at most 10 years. */
const MAX_LOOKBACK_DAYS = 3650;
const WEEK_LOOKBACK_DAYS = 730;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Monday of the week containing the local calendar day `iso` (YYYY-MM-DD). */
export function mondayOfIso(iso: string): string {
  const d = new Date(`${iso}T12:00:00`);
  const offset = (d.getDay() + 6) % 7;
  return addDaysIso(iso, -offset);
}

function nextMonthStart(year: number, month: number): string {
  const y = month === 12 ? year + 1 : year;
  const m = month === 12 ? 1 : month + 1;
  return `${y}-${String(m).padStart(2, '0')}-01`;
}

/**
 * The range to fetch once per granularity, ending today. Weekly: two years back, snapped to a Monday.
 * Monthly and yearly: the first month or January 1st that is not more than ten years back, so the range stays valid.
 */
export function fetchRange(granularity: SummaryGranularity, today: string = isoLocal(new Date())): { from: string; to: string } {
  if (granularity === 'week') return { from: mondayOfIso(addDaysIso(today, -WEEK_LOOKBACK_DAYS)), to: today };
  const earliest = addDaysIso(today, -MAX_LOOKBACK_DAYS);
  const [y, m, d] = earliest.split('-').map(Number);
  if (granularity === 'month') {
    const from = d === 1 ? `${y}-${String(m).padStart(2, '0')}-01` : nextMonthStart(y, m);
    return { from, to: today };
  }
  const janFirst = `${y}-01-01`;
  return { from: earliest <= janFirst ? janFirst : `${y + 1}-01-01`, to: today };
}

/** Last day (inclusive, YYYY-MM-DD) of the period that starts at `bucketStart`. */
export function periodEnd(granularity: SummaryGranularity, bucketStart: string): string {
  if (granularity === 'week') return addDaysIso(bucketStart, 6);
  if (granularity === 'year') return `${bucketStart.slice(0, 4)}-12-31`;
  const [y, m] = bucketStart.split('-').map(Number);
  return addDaysIso(nextMonthStart(y, m), -1);
}

/** "7 Jul 2026" for a YYYY-MM-DD string. Fixed English month names, so labels do not depend on the browser locale. */
export function formatDayIso(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return `${d} ${MONTHS[m - 1]} ${y}`;
}

/** Short axis label for a bucket. */
export function bucketLabel(granularity: SummaryGranularity, bucketStart: string): string {
  const [y, m, d] = bucketStart.split('-').map(Number);
  if (granularity === 'year') return String(y);
  if (granularity === 'month') return `${MONTHS[m - 1]} ${String(y).slice(2)}`;
  return `${d} ${MONTHS[m - 1]} ${String(y).slice(2)}`;
}

/** Long label for a bucket, used in headings and the tooltip. */
export function bucketTitle(granularity: SummaryGranularity, bucketStart: string): string {
  if (granularity === 'year') return bucketStart.slice(0, 4);
  if (granularity === 'month') {
    const [y, m] = bucketStart.split('-').map(Number);
    return `${MONTHS[m - 1]} ${y}`;
  }
  return `Week of ${formatDayIso(bucketStart)}`;
}

/** One chart row per bucket, with the values every metric needs. */
export interface VolumeRow {
  bucketStart: string;
  label: string;
  title: string;
  runs: number;
  distanceKm: number;
  timeH: number;
  durationS: number;
  avgPaceSPerKm: number | null;
  elevationGainM: number | null;
  value: number;
}

export function toVolumeRows(granularity: SummaryGranularity, buckets: ActivitySummaryBucket[], metric: VolumeMetric): VolumeRow[] {
  return buckets.map(b => {
    const distanceKm = Number((b.distanceM / 1000).toFixed(2));
    const timeH = Number((b.durationS / 3600).toFixed(2));
    const value = metric === 'distance' ? distanceKm : metric === 'time' ? timeH : b.runs;
    return {
      bucketStart: b.bucketStart,
      label: bucketLabel(granularity, b.bucketStart),
      title: bucketTitle(granularity, b.bucketStart),
      runs: b.runs,
      distanceKm,
      timeH,
      durationS: b.durationS,
      avgPaceSPerKm: b.avgPaceSPerKm,
      elevationGainM: b.elevationGainM,
      value,
    };
  });
}

/** Axis and tooltip text for a metric value. */
export function formatMetric(metric: VolumeMetric, value: number): string {
  if (metric === 'distance') return `${value.toFixed(1)} km`;
  if (metric === 'time') return `${value.toFixed(1)} h`;
  return String(value);
}

/** Duration for the table and tooltip. Zero time shows a dash. */
export const formatBucketTime = (durationS: number) => (durationS > 0 ? formatDuration(durationS) : '—');
export const formatBucketPace = (pace: number | null) => formatPace(pace);

/** Rows visible for a shift (buckets hidden on the newest side). */
export function visibleRange(total: number, windowBuckets: number, shift: number): { start: number; end: number } {
  const end = Math.max(0, total - shift);
  return { start: Math.max(0, end - windowBuckets), end };
}
